import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";

export type AccessibleUser = {
  guid: string;
  name?: string;
  mail: string;
};

/**
 * Fetch all associated users from the v2 API.
 * Returns the requesting user's own info for regular users,
 * or all CFA-accessible users for corporate users.
 */
async function fetchAllAssociatedUsers(): Promise<AccessibleUser[]> {
  const res = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/v2/associatedUsers/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw error;
  }

  return (await res.json()) as AccessibleUser[];
}

/**
 * React Query hook for fetching all accessible (associated) users.
 * Uses the v2 /associatedUsers/ list endpoint.
 */
export function useAllAccessibleUsers(): {
  data: AccessibleUser[];
  isLoading: boolean;
} {
  const { data = [], isLoading } = useQuery({
    queryKey: queryKeys.accessibleUsers.all,
    queryFn: fetchAllAssociatedUsers,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  return { data, isLoading };
}

/**
 * React Query hook for fetching a single accessible user by ID.
 * Looks up from the all-users cache first.
 */
export function useAccessibleUser(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.accessibleUsers.detail(userId ?? ""),
    queryFn: async (): Promise<AccessibleUser> => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      // Try to find from the all-users cache first
      const allUsers = queryClient.getQueryData<AccessibleUser[]>(
        queryKeys.accessibleUsers.all
      );
      const cached = allUsers?.find((u) => u.guid === userId);
      if (cached) {
        return cached;
      }
      // Fallback: fetch all users and find the one we need
      const users = await fetchAllAssociatedUsers();
      queryClient.setQueryData(queryKeys.accessibleUsers.all, users);
      const found = users.find((u) => u.guid === userId);
      if (found) {
        return found;
      }
      throw new Error(`User ${userId} not found in associated users`);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Prefetch all associated users and populate the cache.
 * Used during app initialization.
 */
export async function fetchAllAssociatedUsersQuery(
  queryClient: QueryClient
): Promise<AccessibleUser[]> {
  const users = await fetchAllAssociatedUsers();
  queryClient.setQueryData(queryKeys.accessibleUsers.all, users);
  // Also populate individual detail caches
  users.forEach((user) => {
    queryClient.setQueryData(queryKeys.accessibleUsers.detail(user.guid), user);
  });
  return users;
}
